"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Smartphone, X } from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { suppressErrorReporting } from "@/lib/observability/report-error";
import { cn } from "@/lib/utils";

const DISMISSAL_KEY = "vidseeds_android_app_banner_dismissed_on";

function getLocalDateKey(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function isAndroidMobileBrowser() {
  if (typeof window === "undefined") return false;

  const ua = navigator.userAgent.toLowerCase();
  const isAndroid = ua.includes("android");
  const isMobile = /mobile|android/.test(ua) || navigator.maxTouchPoints > 0;
  const isNativeApp = window.Capacitor?.isNativePlatform?.() === true;
  const isElectron = window.electronAPI !== undefined;

  return isAndroid && isMobile && !isNativeApp && !isElectron;
}

function wasDismissedToday() {
  try {
    return localStorage.getItem(DISMISSAL_KEY) === getLocalDateKey();
  } catch (error) {
    suppressErrorReporting(
      error instanceof Error ? error.message : "localStorage read failed",
      {
        source: "components.AndroidAppStabilityBanner",
        feature: "android-stability-banner",
        action: "wasDismissedToday",
        expected: true,
      }
    );
    return false;
  }
}

function storeDismissalForToday() {
  try {
    localStorage.setItem(DISMISSAL_KEY, getLocalDateKey());
  } catch (error) {
    suppressErrorReporting(
      error instanceof Error ? error.message : "localStorage write failed",
      {
        source: "components.AndroidAppStabilityBanner",
        feature: "android-stability-banner",
        action: "storeDismissalForToday",
        expected: true,
        extra: { note: "Banner remains session-dismissed via React state." },
      }
    );
  }
}

export function AndroidAppStabilityBanner() {
  const { t } = useJsonTranslation("AndroidAppStabilityBanner");
  const pathname = usePathname();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (pathname === "/download") {
      return;
    }
    if (!isAndroidMobileBrowser() || wasDismissedToday()) {
      return;
    }

    const timer = window.setTimeout(() => setVisible(true), 900);
    return () => {
      window.clearTimeout(timer);
      setVisible(false);
    };
  }, [pathname]);

  const handleDismiss = useCallback(() => {
    storeDismissalForToday();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className={cn(
        "fixed inset-x-0 top-0 z-[9998] px-3 pt-[calc(env(safe-area-inset-top)+0.75rem)]",
        "animate-in slide-in-from-top-3 fade-in duration-300"
      )}
      role="region"
      aria-label={t("ariaLabel")}
    >
      <div className="border-primary/20 bg-background/95 shadow-primary/15 mx-auto flex max-w-md items-start gap-3 rounded-2xl border p-3 shadow-2xl backdrop-blur-xl">
        <div className="from-leaf-500/15 to-primary/15 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br">
          <Smartphone className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground text-sm leading-snug font-semibold">
            {t("title")}
          </p>
          <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
            {t("description")}
          </p>
          <Link
            href="/download"
            className="text-primary mt-2 inline-flex items-center gap-1.5 text-xs font-semibold underline-offset-4 hover:underline"
          >
            {t("download")}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </Link>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground hover:bg-muted/70 -m-1 rounded-lg p-1.5 transition-colors"
          aria-label={t("dismiss")}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
